import moment from 'moment';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { BooleanModel, JsonBooleanModel } from '../primitive/boolean.model';
import { DateModel, JsonDateModel } from '../primitive/date.model';
import { JsonNumberModel, NumberModel } from '../primitive/number.model';
import { JsonTextModel, TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import { RadioModel } from './radio.model';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { RadioModelOptions } from '../options/radio-model-options';

export class ParticipantModel extends CompositeModel {
    protected clientManager: ClientManagerInterface;

    public pos: NumberModel;
    public title: TextModel;
    public firstName: TextModel;
    public lastName: TextModel;
    public mail: TextModel;
    public mobileNumber: TextModel;
    public date: DateModel;
    public trainingName: TextModel;
    public shared: TextModel;
    public trfName: TextModel;
    public trlName: TextModel;
    public duration: TextModel;
    public productName: TextModel;
    public isSelected: BooleanModel;
    public creditType: RadioModel<TextModel>;

    private readonly showMobileNumberForParticipants: boolean;
    private readonly showParticipantsCreditTypeSelectors: boolean;

    public constructor(
        trainingName: string,
        productName: string,
        pos = 0,
        title = '',
        firstName = '',
        lastName = '',
        mail = '',
        mobileNumber = '',
        date: moment.Moment = moment(),
        shared = 'X',
        trfName = '',
        trlName = '',
        duration = '',
        isSelected = true,
        creditType = <string>ConfigConstants.ParticipantCreditType.NOT_AVAILABLE,
        public isCustomData = true,
    ) {
        super('ParticipantModel');
        const injector = CustomInjector.getInjector();
        this.clientManager = injector.get(ClientManagerInstanceToken);
        const configService = injector.get(ConfigService);

        this.pos = new NumberModel('Pos', pos);
        this.showMobileNumberForParticipants = configService.getShowMobileNumberForParticipants();
        this.showParticipantsCreditTypeSelectors = configService.getParticipantCreditTypeSelectorEnabled();

        this.title = <TextModel>this.set(new TextModel('Title', title));
        this.firstName = <TextModel>this.set(new TextModel('FirstName', firstName));
        this.lastName = <TextModel>this.set(new TextModel('LastName', lastName));
        this.mail = <TextModel>this.set(new TextModel('Email', mail));
        this.date = <DateModel>this.set(new DateModel('TrainingStartDate', date));
        this.trainingName = <TextModel>this.set(new TextModel('TrainingName', trainingName));
        this.shared = <TextModel>this.set(new TextModel('Shared', shared));
        this.trfName = <TextModel>this.set(new TextModel('TrainerFirstName', trfName));
        this.trlName = <TextModel>this.set(new TextModel('TrainerLastName', trlName));
        this.duration = <TextModel>this.set(new TextModel('Duration', duration));
        this.productName = <TextModel>this.set(new TextModel('ProductName', productName));

        if (this.showParticipantsCreditTypeSelectors) {
            const creditTypeOptions: Array<DisplayDecorator<TextModel>> = [];

            creditTypeOptions.push(
                new DisplayDecorator(
                    ConfigConstants.ParticipantCreditTypeOptions.NON_CREDIT,
                    new TextModel(
                        ConfigConstants.ParticipantCreditTypeOptions.NON_CREDIT,
                        ConfigConstants.ParticipantCreditType.NON_CREDIT,
                    ),
                ),
            );
            creditTypeOptions.push(
                new DisplayDecorator(
                    ConfigConstants.ParticipantCreditTypeOptions.CREDIT,
                    new TextModel(
                        ConfigConstants.ParticipantCreditTypeOptions.CREDIT,
                        ConfigConstants.ParticipantCreditType.CREDIT,
                    ),
                ),
            );
            creditTypeOptions.push(
                new DisplayDecorator(
                    ConfigConstants.ParticipantCreditTypeOptions.NOT_AVAILABLE,
                    new TextModel(
                        ConfigConstants.ParticipantCreditTypeOptions.NOT_AVAILABLE,
                        ConfigConstants.ParticipantCreditType.NOT_AVAILABLE,
                    ),
                ),
            );

            this.creditType = <RadioModel<TextModel>>this.set(
                new RadioModel<TextModel>(
                    'CreditFlag',
                    creditTypeOptions,
                    new RadioModelOptions({
                        initialSelection: parseInt(
                            creditType || ConfigConstants.ParticipantCreditType.NOT_AVAILABLE,
                            10,
                        ),
                        invalidSelection: parseInt(ConfigConstants.ParticipantCreditType.NOT_AVAILABLE, 10),
                    }),
                ),
            );
        }

        this.isSelected = new BooleanModel('IsSelected', isSelected);
        if (this.showMobileNumberForParticipants) {
            this.mobileNumber = <TextModel>this.set(new TextModel('MobileNumber', mobileNumber));
        }
    }

    public static fromOData(data: ODataParticipantModel): ParticipantModel {
        return new ParticipantModel(
            data.TrainingName,
            data.ProductName,
            data.Counter,
            data.Title,
            data.FirstName,
            data.LastName,
            data.Email,
            data.MobileNumber,
            data.TRDate ? moment(data.TRDate) : moment.invalid(),
            data.Shared,
            data.TRFname,
            data.TRLname,
            data.Duration,
            true,
            data.CreditFlag,
            false,
        );
    }

    public setFieldValidity(): void {
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        modelOptionsLoader.load(this.title, 'ParticipantList', 'Title');
        modelOptionsLoader.load(this.firstName, 'ParticipantList', 'FirstName');
        modelOptionsLoader.load(this.lastName, 'ParticipantList', 'LastName');
        modelOptionsLoader.load(this.mail, 'ParticipantList', 'Email');
        if (this.showMobileNumberForParticipants) {
            modelOptionsLoader.load(this.mobileNumber, 'ParticipantList', 'MobileNumber');
        }
    }

    public onParticipantSelectionChanged(): void {
        this.title = assignModelOptions(this.title, {
            ...this.title.options,
            readonly: !this.isSelected.value,
        });
        this.firstName = assignModelOptions(this.firstName, {
            ...this.firstName.options,
            readonly: !this.isSelected.value,
        });
        this.lastName = assignModelOptions(this.lastName, {
            ...this.lastName.options,
            readonly: !this.isSelected.value,
        });
        this.mail = assignModelOptions(this.mail, {
            ...this.mail.options,
            readonly: !this.isSelected.value,
        });
        if (this.showMobileNumberForParticipants) {
            this.mobileNumber = assignModelOptions(this.mobileNumber, {
                ...this.mobileNumber.options,
                readonly: !this.isSelected.value,
            });
        }
    }
}

export interface ODataParticipantModel extends ODataObject {
    __metadata: {
        uri: string;
        type: 'HC_SM_NGM_58_SRV.Participant';
    };
    Counter: number;
    Title: string;
    FirstName: string;
    LastName: string;
    Email: string;
    MobileNumber: string;
    TRDate: string;
    TrainingName: string;
    Shared: string;
    TRFname: string;
    TRLname: string;
    Duration: string;
    ProductName: string;
    NotificationNo: string;
    TaskNumber: string;
    CreditFlag: string;
}

export class ParticipantModelFromJson extends ParticipantModel {
    public constructor(data: JsonParticipantModel) {
        super(
            data.trainingName.value,
            data.productName.value,
            data.pos.value,
            data.title.value,
            data.firstName.value,
            data.lastName.value,
            data.mail.value,
            data.mobileNumber ? data.mobileNumber.value : '',
            data.date.value ? moment(data.date.value) : moment.invalid(),
            data.shared.value,
            data.trfName.value,
            data.trlName.value,
            data.duration.value,
            data.isSelected ? data.isSelected.value : true,
            data.creditType
                ? data.creditType.selectedEntry.model.value
                : ConfigConstants.ParticipantCreditType.NOT_AVAILABLE,
            data.isCustomData,
        );
    }
}

export interface JsonParticipantModel {
    pos: JsonNumberModel;
    title: JsonTextModel;
    firstName: JsonTextModel;
    lastName: JsonTextModel;
    mail: JsonTextModel;
    mobileNumber: JsonTextModel;
    date: JsonDateModel;
    trainingName: JsonTextModel;
    shared: JsonTextModel;
    trfName: JsonTextModel;
    trlName: JsonTextModel;
    duration: JsonTextModel;
    productName: JsonTextModel;
    isSelected: JsonBooleanModel;
    creditType: RadioModel<TextModel>;
    isCustomData: boolean;
}

export interface ODataParticipantOutModel extends ODataObject {
    Title: string;
    FirstName: string;
    LastName: string;
    Email: string;
    MobileNumber: string;
    Shared: string;
    TrainerFirstName: string;
    TrainerLastName: string;
    TrainingStartDate: string;
    Duration: string;
    TrainingName: string;
    ProductName: string;
    CreditFlag: string;
}
